import type { FC, ReactNode } from 'react'
import type { PaintingAsset } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Eyebrow from '@/components/generals/eyebrow'
import ButtonLink from '@/components/generals/button-link'
import DetailCrop from '@/components/technique/detail-crop'
import PullQuote from '@/components/technique/pull-quote'
import TechniqueChapter from '@/components/technique/technique-chapter'

import { PAINTINGS } from '@/constants/paintings'

interface CaptionProps {
    painting: PaintingAsset
    children: ReactNode
}

const Caption: FC<CaptionProps> = ({ painting, children }): ReactNode => {
    return (
        <>
            <span className='font-heading text-base italic text-parchment/90'>
                {painting.title}, {painting.year}.
            </span>
            {' '}
            {children}
        </>
    )
}

const CHAPTER_LINKS = [
    { href: '#chiaroscuro', label: 'Chiaroscuro' },
    { href: '#impasto', label: 'Impasto' },
    { href: '#psychological-realism', label: 'Psychological realism' }
]

const CLOSE_LOOKS = [
    {
        painting: PAINTINGS.ladyWithFan,
        focus: [0.5, 0.82] as [number, number],
        zoom: 2.6,
        text: 'The feather is a flurry of loaded white strokes, laid down fast and left alone.'
    },
    {
        painting: PAINTINGS.polishNobleman,
        focus: [0.3, 0.5] as [number, number],
        zoom: 2.8,
        text: 'Dabs of thick yellow and white paint catch the light and become glinting gold.'
    },
    {
        painting: PAINTINGS.apostlePaul,
        focus: [0.42, 0.8] as [number, number],
        zoom: 2.6,
        text: 'A few rapid strokes give the hand its weight and the quill its sheen.'
    }
]

const TechniquePage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Technique'
                description='How Rembrandt used chiaroscuro and thick impasto paint to create the psychological realism of his portraits, shown through close-up details of his paintings.'
                ogImage={PAINTINGS.lucretia.src}
            />

            <section id='technique-intro' className='relative overflow-hidden pb-8 pt-16 sm:pt-24'>
                <div aria-hidden='true' className='candle-glow pointer-events-none absolute -right-40 -top-24 size-[38rem] animate-candle opacity-80 motion-reduce:animate-none' />

                <div className='relative mx-auto max-w-7xl px-6 lg:px-10'>
                    <Eyebrow>In the Studio</Eyebrow>

                    <h1 className='mt-6 text-5xl font-medium leading-[1.05] text-parchment sm:text-6xl lg:text-7xl'>
                        His Technique
                    </h1>

                    <p className='mt-6 max-w-2xl text-lg leading-relaxed text-parchment/75'>
                        Stand close to a late Rembrandt and the surface breaks apart into ridges, scratches and smears of pigment. Step back and those marks resolve into skin, breath and thought. Two techniques made it possible, and together they gave his figures an inner life few painters have ever matched.
                    </p>

                    <nav aria-label='Chapters' className='mt-10'>
                        <ol className='flex flex-wrap gap-3'>
                            {CHAPTER_LINKS.map((link, index) => (
                                <li key={link.href}>
                                    <a
                                        href={link.href}
                                        className='inline-flex items-center gap-3 rounded-full bg-parchment/[0.05] px-5 py-2.5 text-sm text-parchment/80 transition-[background-color,color,box-shadow] duration-500 hover:bg-gold/10 hover:text-gold hover:shadow-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold'
                                    >
                                        <span className='font-heading text-gold'>0{index + 1}</span>
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>
                </div>
            </section>

            <TechniqueChapter
                id='chiaroscuro'
                number='01'
                eyebrow='Light and Dark'
                title='Chiaroscuro: directing the eye'
                figure={(
                    <DetailCrop
                        painting={PAINTINGS.philemonAndBaucis}
                        focus={[0.8, 0.5]}
                        zoom={1.9}
                        caption={(
                            <Caption painting={PAINTINGS.philemonAndBaucis}>
                                The light hidden behind Mercury falls on Jupiter&apos;s golden robe and the table.
                            </Caption>
                        )}
                    />
                )}
            >
                <p>
                    Chiaroscuro, Italian for light-dark, is the art of building a picture from strong contrasts of illumination and shadow. Rembrandt absorbed it from his teacher Pieter Lastman and from the Dutch followers of Caravaggio, then made it entirely his own. Instead of lighting a scene evenly, he let a single source, a window, a candle or a hidden lamp, fall where the meaning lies, and allowed everything else to sink into a warm, breathing darkness.
                </p>

                <p>
                    In Philemon and Baucis the light source is hidden altogether. Mercury sits as a dark silhouette against its glow, while the light spills across the table and catches Jupiter&apos;s golden robe at the very moment the gods reveal themselves to their elderly hosts. Your eye goes exactly where Rembrandt wants it, and the shadows invite you to imagine what you cannot quite see.
                </p>

                <aside className='rounded-sm bg-parchment/[0.04] px-6 py-5 text-base leading-relaxed text-parchment/75 shadow-soft'>
                    <span className='block text-[0.68rem] font-medium uppercase tracking-[0.3em] text-gold'>
                        A lasting name
                    </span>
                    <span className='mt-2 block'>
                        Photographers still call a portrait lit from one side, leaving a small triangle of light on the shadowed cheek, Rembrandt lighting.
                    </span>
                </aside>
            </TechniqueChapter>

            <TechniqueChapter
                id='impasto'
                number='02'
                eyebrow='Paint You Can Touch'
                title='Impasto: the body of the paint'
                reversed
                figure={(
                    <DetailCrop
                        painting={PAINTINGS.lucretia}
                        focus={[0.52, 0.66]}
                        zoom={2.4}
                        caption={(
                            <Caption painting={PAINTINGS.lucretia}>
                                Around her waist, paint laid on with a palette knife stands up in glittering ridges.
                            </Caption>
                        )}
                    />
                )}
            >
                <p>
                    Impasto is paint laid on so thickly that it stands up from the surface and catches real light. Where many of his contemporaries polished their pictures smooth, Rembrandt built up highlights in heavy, buttery layers, then dragged, scraped and stippled them with brushes, palette knives and even the wooden end of the brush. The early biographer Arnold Houbraken claimed one portrait was so thickly painted that you could lift it off the floor by its nose.
                </p>

                <p>
                    In Lucretia, painted in 1664, he worked the dress near her waist with a palette knife, laying paint in ridges that glitter like gold thread. Elsewhere he blended wet into wet, or dragged a dry brush across the coarse canvas so that its weave shows through. Up close, the gown dissolves into abstract marks. From a few steps away, it shimmers.
                </p>

                <PullQuote
                    quote='The smell of the paint would bother you.'
                    source='Rembrandt, warning visitors not to look too closely, as recorded by Arnold Houbraken in 1718.'
                />
            </TechniqueChapter>

            <section id='close-looking' aria-labelledby='close-looking-heading' className='relative py-20 sm:py-28'>
                <div aria-hidden='true' className='absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-gold/25 to-transparent' />

                <div className='mx-auto max-w-7xl px-6 lg:px-10'>
                    <div className='max-w-2xl'>
                        <Eyebrow>Look Closer</Eyebrow>

                        <h2 id='close-looking-heading' className='mt-5 text-4xl font-medium leading-[1.1] text-parchment sm:text-5xl'>
                            The brush at work
                        </h2>

                        <p className='mt-6 text-lg leading-relaxed text-parchment/75'>
                            Three more passages from the gallery, enlarged to show how few strokes Rembrandt needed to suggest feather, metal and flesh.
                        </p>
                    </div>

                    <div className='mt-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10'>
                        {CLOSE_LOOKS.map((look) => (
                            <DetailCrop
                                key={look.painting.slug}
                                painting={look.painting}
                                focus={look.focus}
                                zoom={look.zoom}
                                aspect={1}
                                sizes='(min-width: 1024px) 1100px, (min-width: 640px) 120vw, 160vw'
                                caption={<Caption painting={look.painting}>{look.text}</Caption>}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <TechniqueChapter
                id='psychological-realism'
                number='03'
                eyebrow='The Inner Life'
                title='Psychological realism'
                figure={(
                    <DetailCrop
                        painting={PAINTINGS.selfPortrait}
                        focus={[0.56, 0.31]}
                        zoom={2.3}
                        caption={(
                            <Caption painting={PAINTINGS.selfPortrait}>
                                Dense, worked paint models the brow and eyes, while the background stays thin and dark.
                            </Caption>
                        )}
                    />
                )}
            >
                <p>
                    Chiaroscuro and impasto were never ends in themselves. Rembrandt aimed his strongest light at faces and hands, the places where thought and feeling show, and gave those passages his richest, most worked paint, while costumes and backgrounds were left loose and dim. The result is a kind of guided attention: we look where he looked, and we stay there.
                </p>

                <p>
                    In the Self-Portrait of 1659, painted the year after his house and possessions were auctioned, light falls on a face built from dense, overlapping strokes, every furrow of the brow and pouch beneath the eye recorded without flattery. Nothing in the costume distracts. What remains is a man taking honest measure of himself, and it is that honesty, more than any trick of light, that makes his people feel alive nearly four centuries later.
                </p>

                <PullQuote
                    quote='A painting is finished when the master has achieved his intention in it.'
                    source='Rembrandt, as reported by Arnold Houbraken.'
                />
            </TechniqueChapter>

            <section id='technique-cta' className='relative overflow-hidden py-28 sm:py-36'>
                <div aria-hidden='true' className='candle-glow pointer-events-none absolute left-1/2 top-1/2 size-[44rem] -translate-x-1/2 -translate-y-1/2 opacity-70' />

                <div className='relative mx-auto max-w-3xl px-6 text-center'>
                    <Eyebrow align='center'>See for Yourself</Eyebrow>

                    <h2 className='mt-6 text-4xl font-medium leading-[1.1] text-parchment sm:text-5xl'>
                        Now see them whole
                    </h2>

                    <p className='mx-auto mt-6 max-w-xl text-lg leading-relaxed text-parchment/75'>
                        Every detail on this page comes from a painting in the gallery. Step back, take in the whole canvas, and watch the marks come together.
                    </p>

                    <div className='mt-12 flex flex-wrap justify-center gap-4'>
                        <ButtonLink href='/gallery'>
                            Enter the Gallery
                        </ButtonLink>

                        <ButtonLink href='/biography' variant='ghost'>
                            Read his life
                        </ButtonLink>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default TechniquePage
